import { memo, useCallback } from 'react';
import { useSelector } from '@xstate/store/react';
import { ALL_CONNECTOR_SIDES, MIN_SPRITE_SIZE } from '../../entityTypes';
import type { CanvasEntity } from '../../entityTypes';
import { getSpriteData } from '../../entityTypes';
import { useCanvasStores } from '../../stores/CanvasContext';
import { useMergedDropTarget } from '../../hooks/useMergedDropTarget';
import { useLockedLayerIds } from '../../hooks/useLockedLayerIds';
import { canvasSelectionStore } from '../../stores/canvasSelectionStore';
import { useEntityData } from '../../stores/EntityDataContext';
import { CanvasConnector } from '../../components/CanvasConnector';
import { CanvasResizeHandles } from '../../components/CanvasResizeHandles';

export interface CanvasSpriteProps {
  entity: CanvasEntity;
}

function CanvasSpriteInner({ entity }: CanvasSpriteProps) {
  const { store, uiStore } = useCanvasStores();
  const { mergedDropTargetId, mergedDropTargetSide } = useMergedDropTarget();
  const { selectedEdgeConnectors, connectorHasEdge } = useEntityData();
  const lockedLayerIds = useLockedLayerIds();

  const d = getSpriteData(entity);
  const globalClickThrough = useSelector(uiStore, s => s.context.spriteClickThrough);

  const spriteClickThrough = d.clickThrough ?? 'auto';
  const isClickThrough = spriteClickThrough === 'always' ? true
    : spriteClickThrough === 'never' ? false
    : globalClickThrough;

  const isSelected = useSelector(canvasSelectionStore, s => s.context.selectedEntityIds.includes(entity.id));
  const isLocked = lockedLayerIds.has(entity.layerId);
  const isDropTarget = mergedDropTargetId === entity.id;
  const dropTargetSide = isDropTarget ? mergedDropTargetSide : null;

  const handleResize = useCallback((x: number, y: number, width: number, height: number) => {
    store.send({ type: 'entity.resize', entityId: entity.id, x, y, width, height });
  }, [store, entity.id]);

  return (
    <div
      data-canvas-entity
      data-canvas-sprite={entity.id}
      data-entity-id={entity.id}
      data-entity-kind={entity.kind}
      style={{
        position: 'absolute',
        left: entity.x,
        top: entity.y,
        width: entity.width,
        height: entity.height,
        border: d.borderColor ? `${d.borderWidth ?? 1}px solid ${d.borderColor}` : undefined,
        borderRadius: d.borderColor ? 4 : undefined,
        pointerEvents: isClickThrough ? 'none' : undefined,
      }}
      className={`select-none ${isSelected ? 'ring-2 ring-blue-400' : ''} ${isLocked ? 'opacity-60 cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'}`}
    >
      {isClickThrough && (
        <style>{`.sprite-svg-${entity.id} { pointer-events: none !important; } .sprite-svg-${entity.id} * { pointer-events: visiblePainted !important; cursor: ${isLocked ? 'not-allowed' : 'grab'}; }`}</style>
      )}
      <svg
        viewBox={d.viewBox}
        width="100%"
        height="100%"
        className={`sprite-svg-${entity.id}`}
        dangerouslySetInnerHTML={{
          __html: (d.fillMode === 'stroke'
            ? `<style>.sprite-svg-${entity.id} * { stroke-width: ${d.strokeWidth ?? 2} !important; ${d.fixedStroke ? 'vector-effect: non-scaling-stroke !important;' : ''} }</style>`
            : '') + d.svgContent,
        }}
        style={d.fillMode === 'native' ? {} : {
          color: d.color,
          stroke: d.fillMode === 'stroke' ? d.color : 'none',
          fill: d.fillMode === 'fill' ? d.color : 'none',
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
        }}
      />

      {/* Resize handles when selected */}
      {isSelected && !isLocked && (
        <CanvasResizeHandles
          x={entity.x}
          y={entity.y}
          width={entity.width}
          height={entity.height}
          minWidth={MIN_SPRITE_SIZE}
          minHeight={MIN_SPRITE_SIZE}
          onResize={handleResize}
        />
      )}

      {/* Connectors */}
      {entity.connectable && ALL_CONNECTOR_SIDES.map(side => {
        const key = `${entity.id}:${side}`;
        return (
          <CanvasConnector
            key={side}
            taskId={entity.id}

            entityWidth={entity.width}
            entityHeight={entity.height}
            side={side}
            isDropTarget={isDropTarget}
            dropTargetSide={dropTargetSide}
            isEdgeEndpoint={selectedEdgeConnectors.has(key)}
            hasEdge={connectorHasEdge.has(key)}
          />
        );
      })}
    </div>
  );
}

export const CanvasSprite = memo(CanvasSpriteInner);
